import React, { useContext, useMemo, useState } from 'react';

import { CoreSnippetFieldsFragment } from 'models/graphql';
import { SnippetCard } from './SnippetCard';
import { CreateEditSnippetModal } from './CreateEditSnippetModal';
import { SnippetCardViewProps } from './SnippetCard/SnippetCard.view';
import { Loader } from '../../../components/containers/Loader';
import { SnippetsPage } from './SnippetsPage';
import { EmptyPlaceholder } from '../../../components/containers/EmptyPlaceholder';
import NoSnippet from 'assets/images/noSnippets.svg';
import { IconsEnum } from '../../../constants/icons';
import { Button } from 'components/actions/Button';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { ButtonColorsEnum } from 'constants/button-colors';
import { CheckboxWithAction } from 'components/actions/CheckboxWithAction';
import { DeleteSnippetModal } from './DeleteSnippetModal';
import { Tooltip } from 'components/actions/Tooltip';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { BeaconContext } from '../../../providers/BeaconProvider';

export type SnippetsViewProps = {
  idSnippetToDelete: string | null;
  onDeleteClick: (snippetId: string) => void;
  deleteSnippet: () => Promise<void>;
  setSnippetToEdit: (snippetId: string) => void;
  snippets: CoreSnippetFieldsFragment[] | undefined;
  snippetToEdit?: CoreSnippetFieldsFragment | null;
  isCreateSnippetDialogOpen: boolean;
  onCreateSnippetDialogClose: () => void;
  onEditSnippetDialogClose: () => void;
  onAddToPostClick: SnippetCardViewProps['onAddToPostClick'] | undefined;
  onCreateSnippetDialogOpen: VoidFunction;
  isModal?: boolean;
  onDeleteSnippetDialogClose: VoidFunction;
  snippetDeleteLoading: boolean;
};

export const SnippetsView = (props: SnippetsViewProps): JSX.Element => {
  const { beacon } = useContext(BeaconContext);
  const {
    snippetDeleteLoading,
    idSnippetToDelete,
    onDeleteClick,
    deleteSnippet,
    setSnippetToEdit,
    snippetToEdit,
    snippets,
    isCreateSnippetDialogOpen = false,
    onCreateSnippetDialogOpen,
    onDeleteSnippetDialogClose,
    onCreateSnippetDialogClose,
    onEditSnippetDialogClose,
    onAddToPostClick,
    isModal
  } = props;
  const [select, setSelect] = useState(false);
  const breakpoints = useBreakpoints();

  const button = useMemo(() => {
    if (breakpoints === 'small') {
      return (
        <Button
          type={'button'}
          variant={'solid'}
          size={'medium'}
          color={ButtonColorsEnum.primary}
          action={() => onCreateSnippetDialogOpen()}
          icon={{ side: 'center', value: IconsEnum.plus }}
        />
      );
    } else {
      return (
        <Button
          type={'button'}
          variant={'hollow'}
          size={'medium'}
          color={ButtonColorsEnum.primary}
          action={() => onCreateSnippetDialogOpen()}>
          Create a Snippet
        </Button>
      );
    }
  }, [breakpoints, onCreateSnippetDialogOpen]);
  const hasSnippets = snippets && snippets.length > 0;
  return (
    <>
      {hasSnippets && isModal && (
        <div className={'grid-x align-justify margin-bottom-small-16 align-middle'}>
          <div className={'cell shrink'}>
            <div className={'grid-x align-justify align-middle'}>
              <div className={'cell shrink'}>
                <CheckboxWithAction
                  checked={select}
                  name={'add-five-dots'}
                  size={'small'}
                  onClickHandler={() => setSelect(old => !old)}
                  label={'Add 5 dots above the Snippet'}
                />
              </div>

              <div className={'cell shrink'}>
                <Tooltip
                  type={'inlined'}
                  position={'bottom'}
                  pointer
                  target={`#dotsTooltip`}
                  autoZIndex={false}
                  style={{ zIndex: scssVars['z-index-overmodal'] }}>
                  You may wish to separate your hashtags from your caption with a few blank lines - this causes the
                  hashtags to be hidden from view.
                </Tooltip>
              </div>
            </div>
          </div>
          <div className={'cell shrink'}>{button}</div>
        </div>
      )}
      {isCreateSnippetDialogOpen && <CreateEditSnippetModal onClose={onCreateSnippetDialogClose} mode={'create'} />}
      {idSnippetToDelete && (
        <DeleteSnippetModal
          onClose={onDeleteSnippetDialogClose}
          onSubmit={() => deleteSnippet()}
          loading={snippetDeleteLoading}
        />
      )}
      {!!snippetToEdit && (
        <CreateEditSnippetModal
          onClose={onEditSnippetDialogClose}
          defaultContent={snippetToEdit.content as string}
          defaultName={snippetToEdit.name as string}
          snippetToEdit={snippetToEdit}
          mode={'edit'}
        />
      )}

      {hasSnippets ? (
        snippets.map(({ name, content, id }) => (
          <div key={id} className={'margin-bottom-small-8'}>
            <SnippetCard
              {...(onAddToPostClick ? { onAddToPostClick } : {})}
              onDeleteClick={() => onDeleteClick(id)}
              onEditClick={setSnippetToEdit}
              shouldAddDots={select}
              id={id}
              name={name as string}
              content={content as string}
            />
          </div>
        ))
      ) : (
        <EmptyPlaceholder
          removeSpacings={isModal}
          removeBorder={isModal}
          title={`You don't have any Snippets yet...`}
          image={NoSnippet}
          buttonTitle={'Create your first Snippet'}
          buttonIcon={IconsEnum.plus}
          buttonAction={() => onCreateSnippetDialogOpen()}>
          <div>
            <p>
              Save your frequently-used text as a Snippet - whether it's hashtags, people you mention, or links to your website. Then add any Snippet to a Post in one click.<br />
              <a onClick={() => beacon('article', '672', { type: 'modal' })}>Learn more</a>
            </p>
          </div>
        </EmptyPlaceholder>
      )}
    </>
  );
};

export type SnippetsViewLayoutProps = Omit<SnippetsViewProps, 'snippetsLoading'> & {
  snippetsLoading: boolean;
  onDeleteClick: (snippetId: string) => void;
  onCreateSnippetDialogOpen: VoidFunction;
  onDeleteSnippetDialogClose: VoidFunction;
  context: 'MODAL' | 'PAGE';
};

export const SnippetsViewLayout = (props: SnippetsViewLayoutProps): JSX.Element => {
  const { snippetsLoading, context, ...rest } = props;

  switch (context) {
    case 'MODAL':
      return snippetsLoading ? <Loader variation={'dark'} size={'large'} /> : <SnippetsView {...rest} isModal />;
    case 'PAGE':
    default:
      return <SnippetsPage loading={snippetsLoading} {...props} />;
  }
};
